import api from "@/lib/api"

export type MoneyAccountKind = "cash" | "bank" | "clearing"
export type MoneyAccountOption = {
  id: string
  name: string
  kind: MoneyAccountKind
  currency: string
  cash_register_id: string | null
}
export type MoneyAccountRef = Pick<
  MoneyAccountOption,
  "id" | "name" | "kind" | "currency"
>
export type MoneyAccount = MoneyAccountOption & {
  active: boolean
  has_opening: boolean
  bank_name: string | null
  account_holder: string | null
  iban: string | null
  last_verified_at: string | null
  balance_minor: string
  balance: string
  users?: { user_id: number; can_view: boolean; can_use: boolean }[]
}
export type MoneyContext = {
  enabled: boolean
  activated: boolean
  daily_closing_enabled: boolean
  business_date: string
  timezone: string
  activation_date: string | null
  active_account_count?: number
}
export type LegacyPendingReceipt = {
  id: string
  lead_id: string | null
  invoice_id: string | null
  payment_date: string
  currency: string
  gross_amount: string
  allocation_count: number
  receipt_number: string | null
  ambiguous: boolean
  has_proof: boolean
}
export type MoneySetup = MoneyContext & {
  pending_legacy_receipts: LegacyPendingReceipt[]
  pending_legacy_count: number
  pending_legacy_ambiguous_count: number
  pending_legacy_review_token: string | null
}
export type CashRegister = {
  id: string
  name: string
  location: string | null
  active: boolean
}
export type SessionRegister = Pick<CashRegister, "id" | "name" | "location"> & {
  accounts: { id: string; name: string; currency: string }[]
  open_session_id: string | null
  open_business_date: string | null
}
export type MoneyOperator = {
  id: number
  full_name: string
  can_use_accounts: boolean
  can_view_accounts: boolean
  has_full_access: boolean
}
export type MoneyMovement = {
  id: string
  operation_id: string
  kind: string
  source_type: string | null
  source_id: string | null
  reference: string | null
  recorded_by: { id: number; full_name: string } | null
  business_date: string
  occurred_at: string | null
  posted_at: string | null
  signed_amount_minor: string
  amount: string
  currency: string
  reversal_of_operation_id: string | null
}
export type MoneyTransfer = {
  id: string
  from_account: MoneyAccountRef | null
  to_account: MoneyAccountRef | null
  clearing_account: MoneyAccountRef | null
  amount: string
  currency: string
  status: "pending" | "completed" | "cancelled"
  effective_status: "pending" | "completed" | "cancelled" | "reversed"
  correction_id: string | null
  reference: string | null
  purpose: string | null
  created_at: string
  updated_at: string
}
export type MoneyAdjustmentDetail = {
  id: string
  account: MoneyAccountRef
  amount: string
  currency: string
  direction: "top_up" | "withdrawal"
  classification: string
  counterparty: string | null
  purpose: string
  reference: string | null
  effective_status: "settled" | "reversed"
  correction_id: string | null
  created_at: string
}
export type CardSettlementDetail = {
  id: string
  clearing_account: MoneyAccountRef
  bank_account: MoneyAccountRef
  gross_amount: string
  net_amount: string
  fee_amount: string
  currency: string
  reference: string | null
  effective_status: "settled" | "reversed"
  correction_id: string | null
  created_at: string
}
export type CashCount = {
  id: string
  kind: string
  account_id: string
  account_name: string
  currency: string
  expected_minor: string
  expected_amount: string
  counted_minor: string
  counted_amount: string
  difference_minor: string
  difference_amount: string
  reason: string | null
  adjustment_operation_id: string | null
}
export type CashSession = {
  id: string
  cash_register_id: string
  register_name: string
  business_date: string
  business_timezone: string
  status: "open" | "closed"
  opened_at: string
  opened_by: { id: number; full_name: string } | null
  closed_at: string | null
  closed_by: { id: number; full_name: string } | null
  counts: CashCount[]
}
export type BankVerification = {
  id: string
  account_id: string
  currency: string
  statement_date: string
  recorded_minor: string
  recorded_balance: string
  statement_minor: string
  statement_balance: string
  difference_minor: string
  difference: string
  reason: string | null
  adjustment_operation_id: string | null
  verified_at: string
  verified_by: { id: number; full_name: string } | null
}
export type Page<T> = {
  data: T[]
  current_page: number
  last_page: number
  total: number
}
export type ResourcePage<T> = {
  data: T[]
  meta: { current_page: number; last_page: number; total: number }
}
const get = async <T>(url: string, params?: object): Promise<T> =>
  (await api.get<{ data: T }>(url, { params })).data.data
const post = async <T>(url: string, body: object): Promise<T> =>
  (await api.post<{ data: T }>(url, body)).data.data
const patch = async <T>(url: string, body: object): Promise<T> =>
  (await api.patch<{ data: T }>(url, body)).data.data
const page = async <T>(
  url: string,
  params?: object,
): Promise<ResourcePage<T>> =>
  (await api.get<ResourcePage<T>>(url, { params })).data

export const newMoneyKey = () => crypto.randomUUID()
export const getMoneyContext = () => get<MoneyContext>("/money/context")
export const getMoneySetup = () => get<MoneySetup>("/money/setup")
export const activateMoneySetup = (body: {
  activation_date: string
  verified: true
  openings: { account_id: string; amount: string }[]
  pending_receipts_verified?: true
  pending_receipts_review_token?: string
}) => post<MoneyContext>("/money/setup/activate", body)
export const getMoneyAccountOptions = (params?: {
  kind?: MoneyAccountKind
  currency?: string
}) => get<MoneyAccountOption[]>("/money/accounts/options", params)
export const getMoneyAccounts = () => get<MoneyAccount[]>("/money/accounts")
export const getMoneyAccount = (id: string) =>
  get<MoneyAccount>(`/money/accounts/${id}`)
export const createMoneyAccount = (body: {
  name: string
  kind: MoneyAccountKind
  currency: string
  cash_register_id?: string
  bank_name?: string
  account_holder?: string
  iban?: string
  idempotency_key: string
  opening_amount?: string
  opening_verified?: true
}) => post<MoneyAccount>("/money/accounts", body)
export const updateMoneyAccount = (
  id: string,
  body: Partial<
    Pick<
      MoneyAccount,
      | "name"
      | "kind"
      | "currency"
      | "cash_register_id"
      | "bank_name"
      | "account_holder"
      | "iban"
      | "active"
    >
  > & { opening_amount?: string; opening_verified?: true },
) => patch<MoneyAccount>(`/money/accounts/${id}`, body)
export const setMoneyAccountUsers = (
  id: string,
  users: { user_id: number; can_view: boolean; can_use: boolean }[],
) =>
  api
    .put<{ data: MoneyAccount }>(`/money/accounts/${id}/users`, { users })
    .then((r) => r.data.data)
export const getMoneyMovements = (id: string, pageNumber = 1) =>
  api
    .get<
      Page<MoneyMovement>
    >(`/money/accounts/${id}/movements`, { params: { page: pageNumber } })
    .then((r) => r.data)
export const getMoneyOperators = () => get<MoneyOperator[]>("/money/operators")
export const getCashRegisters = () => get<CashRegister[]>("/money/registers")
export const createCashRegister = (body: { name: string; location?: string }) =>
  post<CashRegister>("/money/registers", body)
export const updateCashRegister = (
  id: string,
  body: Partial<Pick<CashRegister, "name" | "location" | "active">>,
) => patch<CashRegister>(`/money/registers/${id}`, body)
export const getSessionRegisters = () =>
  api
    .get<{
      data: SessionRegister[]
      business_date: string
      daily_closing_enabled: boolean
    }>("/money/session-registers")
    .then((r) => r.data)
export const getCurrentSession = (id: string) =>
  api
    .get<{
      data: CashSession | null
      business_date: string
      daily_closing_enabled: boolean
    }>(`/money/registers/${id}/sessions/current`)
    .then((r) => r.data)
export const getSessionHistory = (id: string, pageNumber = 1) =>
  page<CashSession>(`/money/registers/${id}/sessions`, { page: pageNumber })
export const getCashSession = (id: string) =>
  get<CashSession>(`/money/cash-sessions/${id}`)
export const openCashSession = (
  id: string,
  body: {
    business_date: string
    idempotency_key: string
    counts: { account_id: string; counted_amount: string; reason?: string }[]
  },
) => post<CashSession>(`/money/registers/${id}/sessions`, body)
export const closeCashSession = (
  id: string,
  body: {
    idempotency_key: string
    adjust_variance: boolean
    counts: { account_id: string; counted_amount: string; reason?: string }[]
  },
) => post<CashSession>(`/money/cash-sessions/${id}/close`, body)
export const getMoneyTransfers = (params?: {
  status?: "pending" | "completed" | "cancelled" | "reversed"
  page?: number
}) => page<MoneyTransfer>("/money/transfers", params)
export const createMoneyTransfer = (body: {
  from_account_id: string
  to_account_id: string
  clearing_account_id?: string
  amount: string
  purpose?: string
  reference?: string
  idempotency_key: string
}) => post<MoneyTransfer>("/money/transfers", body)
export const finishMoneyTransfer = (
  id: string,
  action: "complete" | "cancel",
  key: string,
) =>
  post<MoneyTransfer>(`/money/transfers/${id}/${action}`, {
    idempotency_key: key,
  })
export type MoneyCorrection = {
  source_type: string
  source_id: string
  source_status: string
  effective_status: "reversed"
  correction_id: string
  operation_ids: string[]
}
export const reverseMoneyTransfer = (
  id: string,
  body: { idempotency_key: string; reason?: string },
) => post<MoneyCorrection>(`/money/transfers/${id}/reverse`, body)
export const reverseMoneyAdjustment = (
  id: string,
  body: { idempotency_key: string; reason?: string },
) => post<MoneyCorrection>(`/money/adjustments/${id}/reverse`, body)
export const reverseCardSettlement = (
  id: string,
  body: { idempotency_key: string; reason?: string },
) => post<MoneyCorrection>(`/money/card-settlements/${id}/reverse`, body)
export const getMoneyAdjustment = (id: string) =>
  get<MoneyAdjustmentDetail>(`/money/adjustments/${id}`)
export const getCardSettlement = (id: string) =>
  get<CardSettlementDetail>(`/money/card-settlements/${id}`)
export const createMoneyAdjustment = (body: {
  money_account_id: string
  amount: string
  direction: "top_up" | "withdrawal"
  classification:
    | "owner_contribution"
    | "owner_draw"
    | "non_expense"
    | "expense"
    | "other"
  counterparty?: string
  purpose: string
  reference?: string
  idempotency_key: string
}) => post<unknown>("/money/adjustments", body)
export const createCardSettlement = (body: {
  clearing_account_id: string
  bank_account_id: string
  gross_amount: string
  net_amount: string
  fee_amount: string
  reference?: string
  idempotency_key: string
}) => post<unknown>("/money/card-settlements", body)
export const getBankVerifications = (id: string, pageNumber = 1) =>
  page<BankVerification>(`/money/accounts/${id}/verifications`, {
    page: pageNumber,
  })
export const createBankVerification = (
  id: string,
  body: {
    statement_date: string
    statement_balance: string
    reason?: string
    adjust_difference: boolean
    idempotency_key: string
  },
) => post<BankVerification>(`/money/accounts/${id}/verifications`, body)
export async function getCashSessionDocument(
  id: string,
  format: "print" | "export.csv",
): Promise<string> {
  const response = await api.get<Blob>(`/money/cash-sessions/${id}/${format}`, {
    responseType: "blob",
    headers: { Accept: format === "print" ? "application/pdf" : "text/csv" },
  })
  return URL.createObjectURL(response.data)
}
